/**
 * Timezone Helpers
 * Wrapper functions for timezone conversions
 * Uses moment-timezone and existing utility functions
 */

import moment from 'moment';
import momentTz from 'moment-timezone';
import { getTimezoneOffset } from 'utils/utility_functions/timezone';

// Default timezone if none specified
const DEFAULT_TIMEZONE = momentTz.tz.guess();

/**
 * Get user's timezone from context
 *
 * @param {object} userContext - User context object
 * @returns {string} Timezone string (e.g., 'America/Chicago')
 */
export const getUserTimezone = (userContext) => {
  if (!userContext || !userContext.userDetails || !userContext.userDetails.timezone) {
    return DEFAULT_TIMEZONE;
  }

  return userContext.userDetails.timezone;
};

/**
 * Convert UTC date to user's timezone
 *
 * @param {string|Date} utcDate - UTC date
 * @param {string} timezone - Target timezone
 * @returns {string} Date string in user timezone (ISO format)
 */
export const convertToUserTimezone = (utcDate, timezone) => {
  if (!utcDate) {
    return '';
  }

  try {
    const tz = timezone || DEFAULT_TIMEZONE;

    // Parse the date and convert to specified timezone
    const momentDate = momentTz.utc(utcDate).tz(tz);

    return momentDate.format();
  } catch (error) {
    console.error('Error converting timezone:', error);
    return utcDate.toString();
  }
};

/**
 * Format date for API request
 *
 * @param {string|Date} date - Date to format
 * @param {string} timezone - User timezone
 * @param {boolean} includeTime - Include time in output
 * @returns {string} Formatted date string
 */
export const formatDateForAPI = (date, timezone, includeTime = false) => {
  if (!date) {
    return '';
  }

  try {
    const tz = timezone || DEFAULT_TIMEZONE;
    const momentDate = moment(date).tz(tz);

    if (includeTime) {
      return momentDate.format('YYYY-MM-DDTHH:mm:ssZ');
    }

    return momentDate.format('YYYY-MM-DD');
  } catch (error) {
    console.error('Error formatting date:', error);
    return moment(date).format('YYYY-MM-DD');
  }
};

/**
 * Get current date in specified timezone
 *
 * @param {string} timezone - Timezone
 * @returns {Date} Current date in timezone
 */
export const getCurrentDateInTimezone = (timezone) => {
  const tz = timezone || DEFAULT_TIMEZONE;
  return momentTz().tz(tz).toDate();
};

/**
 * Get timezone offset for API calls
 * Wrapper for existing utility function
 *
 * @param {string} timezone - Timezone
 * @returns {string} Timezone offset (e.g., '-06:00')
 */
export const getTimezoneOffsetForAPI = (timezone) => {
  try {
    return getTimezoneOffset(timezone);
  } catch (error) {
    console.error('Error getting timezone offset:', error);
    return '-06:00'; // Default to CST
  }
};

/**
 * Parse date range from FullCalendar view info
 *
 * @param {object} viewInfo - FullCalendar view info
 * @param {string} timezone - User timezone
 * @returns {object} {start: string, end: string}
 */
export const parseDateRange = (viewInfo, timezone) => {
  if (!viewInfo || !viewInfo.start || !viewInfo.end) {
    return { start: null, end: null };
  }

  const tz = timezone || DEFAULT_TIMEZONE;

  const start = momentTz(viewInfo.start).tz(tz).format('YYYY-MM-DD');
  const end = momentTz(viewInfo.end).tz(tz).format('YYYY-MM-DD');

  return { start, end };
};

/**
 * Format date range for display
 *
 * @param {Date|string} start - Start date
 * @param {Date|string} end - End date
 * @param {string} timezone - User timezone
 * @returns {string} Formatted date range string
 */
export const formatDateRangeForDisplay = (start, end, timezone) => {
  if (!start) {
    return '';
  }

  const tz = timezone || DEFAULT_TIMEZONE;
  const startMoment = momentTz(start).tz(tz);
  const endMoment = end ? momentTz(end).tz(tz) : null;

  // Same day
  if (endMoment && startMoment.isSame(endMoment, 'day')) {
    return startMoment.format('MMMM D, YYYY');
  }

  // Same month
  if (endMoment && startMoment.isSame(endMoment, 'month')) {
    return `${startMoment.format('MMMM D')} - ${endMoment.format('D, YYYY')}`;
  }

  // Different months
  if (endMoment) {
    return `${startMoment.format('MMM D')} - ${endMoment.format('MMM D, YYYY')}`;
  }

  // Single date
  return startMoment.format('MMMM D, YYYY');
};

/**
 * Format date range for display based on calendar view type
 *
 * @param {Date|string} start - Start date
 * @param {Date|string} end - End date
 * @param {string} timezone - User timezone
 * @param {string} viewType - Calendar view type ('timeGridDay', 'timeGridWeek', 'dayGridMonth', 'multiMonthYear', 'listWeek')
 * @returns {string} Formatted date range string specific to view
 */
export const formatDateRangeForView = (start, end, timezone, viewType) => {
  if (!start) {
    return '';
  }

  const tz = timezone || DEFAULT_TIMEZONE;
  const startMoment = momentTz(start).tz(tz);
  const endMoment = end ? momentTz(end).tz(tz) : null;

  switch (viewType) {
    case 'multiMonthYear': // Year view
      return startMoment.format('YYYY');

    case 'dayGridMonth': // Month view
      return startMoment.format('MMMM YYYY');

    case 'timeGridWeek': // Week view
      if (endMoment) {
        // If start and end are in the same month
        if (startMoment.isSame(endMoment, 'month')) {
          return `${startMoment.format('MMM D')} – ${endMoment.format('D, YYYY')}`;
        } else {
          // If start and end span different months
          return `${startMoment.format('MMM D')} – ${endMoment.format('MMM D, YYYY')}`;
        }
      }
      return startMoment.format('MMM D, YYYY');

    case 'timeGridDay': // Day view
      return startMoment.format('MMMM D, YYYY');

    case 'listWeek': // Agenda view
      if (endMoment) {
        return `${startMoment.format('MMM D')} - ${endMoment.format('MMM D, YYYY')}`;
      }
      return startMoment.format('MMM D, YYYY');

    default:
      // Fallback to generic formatting
      return formatDateRangeForDisplay(start, end, timezone);
  }
};

/**
 * Check if date is today in user's timezone
 *
 * @param {Date|string} date - Date to check
 * @param {string} timezone - User timezone
 * @returns {boolean} True if date is today
 */
export const isToday = (date, timezone) => {
  const tz = timezone || DEFAULT_TIMEZONE;
  const dateMoment = momentTz(date).tz(tz);
  const today = momentTz().tz(tz);

  return dateMoment.isSame(today, 'day');
};

/**
 * Get day of week name
 *
 * @param {Date|string} date - Date
 * @param {string} timezone - User timezone
 * @returns {string} Day name (e.g., 'Monday')
 */
export const getDayOfWeek = (date, timezone) => {
  const tz = timezone || DEFAULT_TIMEZONE;
  return momentTz(date).tz(tz).format('dddd');
};
